import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client";
import type { CostSummaryResponse, MeterView, Reading, Series } from "../api/types";
import { transportLabels } from "../api/types";
import { formatNumber, HistoryChart } from "./HistoryChart";
import { StatusBadge } from "./StatusBadge";
import { CostSummary } from "./CostSummary";

/** Цвета фаз закреплены жёстко: L1 всегда синяя, L2 оранжевая, L3 бирюзовая. */
const PHASE_COLORS = ["var(--series-1)", "var(--series-2)", "var(--series-3)"];
const SINGLE_COLOR = ["var(--series-1)"];

const RANGES = [
  { key: "15m", label: "15 мин" },
  { key: "1h", label: "1 час" },
  { key: "6h", label: "6 часов" },
  { key: "24h", label: "24 часа" },
];

/** Группы графиков: одна ось значений на карточку. */
const GROUPS = [
  { title: "Напряжение по фазам", metrics: ["voltage_l1", "voltage_l2", "voltage_l3"], colors: PHASE_COLORS, digits: 1 },
  { title: "Ток по фазам", metrics: ["current_l1", "current_l2", "current_l3"], colors: PHASE_COLORS, digits: 1, fromZero: true },
  { title: "Активная мощность", metrics: ["power_active"], colors: SINGLE_COLOR, digits: 2, fromZero: true },
  { title: "Энергия (нарастающим итогом)", metrics: ["energy_import"], colors: SINGLE_COLOR, digits: 1 },
];

const CHART_METRICS = GROUPS.flatMap((g) => g.metrics);

/** Порядок плиток с текущими значениями. */
const TILE_METRICS = [
  "voltage_l1", "voltage_l2", "voltage_l3",
  "current_l1", "current_l2", "current_l3",
  "power_active", "power_reactive", "power_apparent", "power_factor",
  "frequency", "energy_import", "energy_export",
];

const UNITS: Record<string, string> = {
  voltage_l1: "В", voltage_l2: "В", voltage_l3: "В",
  current_l1: "А", current_l2: "А", current_l3: "А",
  power_active: "кВт", power_reactive: "квар", power_apparent: "кВА", power_factor: "",
  frequency: "Гц", energy_import: "кВт·ч", energy_export: "кВт·ч",
};

const LABELS: Record<string, string> = {
  voltage_l1: "Напряжение L1", voltage_l2: "Напряжение L2", voltage_l3: "Напряжение L3",
  current_l1: "Ток L1", current_l2: "Ток L2", current_l3: "Ток L3",
  power_active: "Активная мощность", power_reactive: "Реактивная мощность",
  power_apparent: "Полная мощность", power_factor: "Коэффициент мощности",
  frequency: "Частота", energy_import: "Энергия (потребление)", energy_export: "Энергия (отдача)",
};

const DIGITS: Record<string, number> = {
  power_factor: 3, frequency: 2, energy_import: 1, energy_export: 1,
};

export function MeterDetail({
  meter,
  lastReading,
  offline,
}: {
  meter: MeterView;
  lastReading: Reading | null;
  offline: boolean;
}) {
  const [range, setRange] = useState("15m");
  const [series, setSeries] = useState<Series[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Сырые ли точки в текущем ответе: только тогда имеет смысл дорисовывать
  // живые замеры. Для прореженных данных сервер отдаёт средние по интервалу,
  // и добавление сырой точки исказило бы ряд.
  const rawPoints = useRef(true);
  const windowMs = useMemo(() => parseRange(range), [range]);

  // Сводка стоимости за сутки и месяц. Не зависит от периода графика: это
  // «сколько потрачено», а не окно наблюдения. null — ещё не загружено;
  // pricingOff — подсчёт выключен на бэкенде (ручка вернула 404), блок скрыт.
  const [costSummary, setCostSummary] = useState<CostSummaryResponse | null>(null);
  const [pricingOff, setPricingOff] = useState(false);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.history(meter.id, range, CHART_METRICS, signal);
        rawPoints.current = res.bucket === "0s";
        setSeries(res.series);
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [meter.id, range],
  );

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    // Периодическая перезагрузка приводит график в соответствие с базой:
    // живые точки дорисовываются локально и не учитывают прореживание.
    const t = window.setInterval(() => load(), 60_000);
    return () => {
      ac.abort();
      window.clearInterval(t);
    };
  }, [load]);

  // Сводка стоимости. Обновляем при смене счётчика и раз в 30 с — цена меняется
  // медленно, а потребление растёт плавно. От периода графика не зависит.
  useEffect(() => {
    if (pricingOff) return;
    const ac = new AbortController();
    const loadCost = async () => {
      try {
        setCostSummary(await api.costSummary(meter.id, ac.signal));
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
        // 404 — подсчёт стоимости выключен в конфиге; прячем блок молча.
        if ((e as Error).message.includes("404") || /выключен/.test((e as Error).message)) {
          setPricingOff(true);
        }
      }
    };
    loadCost();
    const t = window.setInterval(loadCost, 30_000);
    return () => {
      ac.abort();
      window.clearInterval(t);
    };
  }, [meter.id, pricingOff]);

  // Дорисовка живых замеров: график движется сразу, без ожидания перезапроса.
  useEffect(() => {
    if (!lastReading || lastReading.meterId !== meter.id || !rawPoints.current) return;
    const ts = new Date(lastReading.ts).getTime();
    if (Number.isNaN(ts)) return;

    setSeries((prev) =>
      prev.map((s) => {
        const v = lastReading.values[s.metric];
        if (v === undefined) return s;
        if (s.ts.length && ts <= s.ts[s.ts.length - 1]) return s; // дубликат или замер задним числом

        const cutoff = ts - windowMs;
        let start = 0;
        while (start < s.ts.length && s.ts[start] < cutoff) start++;

        return {
          ...s,
          ts: [...s.ts.slice(start), ts],
          values: [...s.values.slice(start), v],
        };
      }),
    );
  }, [lastReading, meter.id, windowMs]);

  const byMetric = useMemo(() => {
    const m = new Map<string, Series>();
    for (const s of series) m.set(s.metric, s);
    return m;
  }, [series]);

  return (
    <>
      <section className="panel">
        <header>
          <h2>{meter.name}</h2>
          <StatusBadge state={meter.state} interval={meter.intervalMs} />
          <span className="badge">{transportLabels[meter.transport] ?? meter.transport}</span>
          <span className="badge">{meter.address}</span>
          {meter.unitId > 0 && (
            <span className="badge" title="Адрес прибора на шине RS-485">
              адрес {meter.unitId}
            </span>
          )}
          {meter.regMap && <span className="badge">карта: {meter.regMap}</span>}
          <div className="spacer" style={{ flex: 1 }} />
          <div className="range-picker" role="group" aria-label="Период">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                aria-pressed={range === r.key}
                onClick={() => setRange(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </header>

        <div className="muted" style={{ marginBottom: 12 }}>
          {meter.location && <>{meter.location} · </>}
          сер. № {meter.serial || "—"}
          {meter.latestTs && <> · замер {new Date(meter.latestTs).toLocaleTimeString("ru-RU")}</>}
        </div>

        {!meter.state.online && meter.state.lastError && (
          <div className="error-banner">Ошибка связи: {meter.state.lastError}</div>
        )}

        <div className="tiles">
          {TILE_METRICS.filter((k) => meter.latest[k] !== undefined).map((k) => (
            <div className="tile" key={k}>
              <div className="label">
                <span className="swatch" style={{ background: swatchFor(k) }} aria-hidden="true" />
                {LABELS[k] ?? k}
              </div>
              <div className="value">
                {formatNumber(meter.latest[k], DIGITS[k] ?? 1)}
                {UNITS[k] && <span className="unit">{UNITS[k]}</span>}
              </div>
            </div>
          ))}
        </div>

        {!pricingOff && costSummary && <CostSummary summary={costSummary} />}
      </section>

      {error &&
        (offline ? (
          <div className="muted" style={{ marginBottom: 12 }}>
            Графики истории недоступны без связи — показаны последние значения выше.
          </div>
        ) : (
          <div className="error-banner">Не удалось загрузить историю: {error}</div>
        ))}

      <div className="charts">
        {GROUPS.map((g) => (
          <HistoryChart
            key={g.title}
            title={g.title}
            series={g.metrics.map((k) => byMetric.get(k)).filter((s): s is Series => Boolean(s))}
            colors={g.colors}
            digits={g.digits}
            fromZero={g.fromZero}
          />
        ))}
      </div>

      {loading && series.length === 0 && <div className="muted">Загружаем историю…</div>}
    </>
  );
}

/** Цвет плитки повторяет цвет фазы на графике, у остальных метрик — без метки. */
function swatchFor(metric: string): string {
  if (metric.endsWith("_l1")) return "var(--series-1)";
  if (metric.endsWith("_l2")) return "var(--series-2)";
  if (metric.endsWith("_l3")) return "var(--series-3)";
  return "transparent";
}

function parseRange(range: string): number {
  const m = /^(\d+)([mhd])$/.exec(range);
  if (!m) return 15 * 60_000;
  const n = Number(m[1]);
  const unit = { m: 60_000, h: 3_600_000, d: 86_400_000 }[m[2]] ?? 60_000;
  return n * unit;
}
